import { environment } from '../../environments/environment.prod.js'
import { authService } from '../auth/auth-service.js'

const BASE = environment.api.baseUrl

async function authFetch(path: string, init?: RequestInit): Promise<Response> {
  const token = authService.getAccessToken()
  if (!token) throw new Error('Not authenticated')

  return fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${token}`,
      ...init?.headers,
    },
  })
}

export interface InviteResponse {
  success: boolean
  inviteId?: string
  error?: string
}

export interface ClaimResponse {
  success: boolean
  entityType?: 'team' | 'plan'
  entityId?: string
  entityName?: string
  teamId?: string
  teamName?: string
  planId?: string
  planName?: string
  error?: string
}

export interface Notification {
  id: string
  type: string
  referenceId: string
  inviteToken: string
  entityType?: 'team' | 'plan'
  entityId?: string
  entityName?: string
  teamId?: string
  teamName?: string
  planId?: string
  planName?: string
  invitedBy: string
  invitedByName: string
  read: boolean
  createdAt: string
}

export async function sendTeamInvite(
  teamId: string,
  email: string,
  role: 'owner' | 'member' = 'member'
): Promise<InviteResponse> {
  const res = await authFetch(`/api/teams/${teamId}/invite`, {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  })
  if (!res.ok) {
    return { success: false, error: `HTTP ${res.status}: ${res.statusText}` }
  }
  return res.json()
}

export async function sendPlanInvite(
  planId: string,
  email: string,
  role: 'leader' | 'member' | 'viewer' = 'member'
): Promise<InviteResponse> {
  const res = await authFetch(`/api/plans/${planId}/invite`, {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  })
  if (!res.ok) {
    return { success: false, error: `HTTP ${res.status}: ${res.statusText}` }
  }
  return res.json()
}

export async function claimInvite(token: string): Promise<ClaimResponse> {
  const res = await authFetch(`/api/invite/${token}/claim`, {
    method: 'POST',
  })
  return res.json()
}

export async function declineInvite(token: string): Promise<{ success: boolean }> {
  const res = await authFetch(`/api/invite/${token}/decline`, {
    method: 'POST',
  })
  return res.json()
}

export async function getNotifications(): Promise<Notification[]> {
  const res = await authFetch('/api/notifications')
  const data = await res.json()
  return data.notifications ?? []
}

export async function markNotificationRead(id: string): Promise<void> {
  await authFetch(`/api/notifications/${id}/read`, { method: 'POST' })
}

export async function deleteNotification(id: string): Promise<void> {
  await authFetch(`/api/notifications/${id}`, { method: 'DELETE' })
}
